import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSupervisor?: boolean;
  requireAdminOrLimitedAdmin?: boolean;
  allowedEmails?: string[]; 
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false, requireSupervisor = false, requireAdminOrLimitedAdmin = false, allowedEmails }) => {
  const { isAuthenticated, isAdmin, isSupervisor, isLimitedAdmin, isLoading, isGuestAccess, user } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Secret email-based routes (highest priority check)
  if (allowedEmails && allowedEmails.length > 0) {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (!allowedEmails.includes(user.email.toLowerCase())) {
      return <Navigate to="/unauthorized" replace />;
    }
    return <>{children}</>;
  }

  // Admin routes require actual authentication (not guest access)
  if (requireAdmin) {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (!isAdmin) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Supervisor routes (supervisors can also access if they're admins)
  if (requireSupervisor) {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (!isSupervisor && !isAdmin && !isLimitedAdmin) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Admin or Limited Admin routes
  if (requireAdminOrLimitedAdmin) {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (!isAdmin && !isLimitedAdmin) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute; 